// Globals
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Components
import { Modal, ModalSize } from '@/components';
import { Button, Select } from '@/ui';
import { StyledButtonsWrapper, StyledTitleModal } from './ChangeStatusModalStyles';

type TRegionOption = {
  label: string;
  value: string;
};

type TChangeStatusModal = {
  isOpened: boolean;
  isLoading: boolean;
  nextStatus: string;
  closeCancelOrderModal: () => void;
  handleChangeOrderStatus: (regionId?: string) => Promise<void>;
  requiresRegionSelection?: boolean;
  regionOptions?: TRegionOption[];
  onSearchRegions?: (value: string) => void;
  defaultRegionId?: string | null;
};

const ChangeStatusModal = ({
  isOpened,
  isLoading,
  nextStatus,
  closeCancelOrderModal,
  handleChangeOrderStatus,
  requiresRegionSelection = false,
  regionOptions = [],
  onSearchRegions,
  defaultRegionId,
}: TChangeStatusModal) => {
  const { t } = useTranslation(['orders']);
  const [selectedRegionId, setSelectedRegionId] = useState<string | undefined>();

  useEffect(() => {
    if (isOpened) {
      setSelectedRegionId(defaultRegionId ?? undefined);
    }
  }, [isOpened, defaultRegionId]);

  const canConfirm = !requiresRegionSelection || Boolean(selectedRegionId);

  return (
    <Modal visible={isOpened} onCancel={closeCancelOrderModal} size={ModalSize.LARGE}>
      <StyledTitleModal>
        {t('modal_change_status_title', {
          status: t(`status_${nextStatus}`),
        })}
      </StyledTitleModal>
      {requiresRegionSelection && (
        <>
          <StyledTitleModal>{t('order_details_unti')}</StyledTitleModal>
          <Select
            options={regionOptions}
            size="large"
            filterOption={false}
            onSearch={onSearchRegions}
            value={selectedRegionId}
            allowClear
            showSearch
            onChange={(value) => setSelectedRegionId(value as string | undefined)}
            placeholder={t('oreder_search_region_placeholder')}
            style={{ width: '100%', marginBottom: '2rem' }}
          />
        </>
      )}
      <StyledButtonsWrapper>
        <Button onClick={closeCancelOrderModal} loading={isLoading} type="primary">
          {t('modal_reject_btn')}
        </Button>
        <Button
          onClick={() => handleChangeOrderStatus(selectedRegionId)}
          loading={isLoading}
          type="text"
          disabled={!canConfirm}
        >
          {t('modal_accept_btn')}
        </Button>
      </StyledButtonsWrapper>
    </Modal>
  );
};

export default ChangeStatusModal;
